import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, finalize } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Reads the portal assistant's answers aloud (al-tutor `POST /portal-agent/speak`,
 * the same synthesis as the chat widget's read-aloud — crud/tts_ops.py).
 * One answer plays at a time; asking for another stops the first.
 * Docs: features/docs/technical/portal-agent-native.md (Panel).
 */
@Injectable({ providedIn: 'root' })
export class AgentVoiceService {
  private readonly http = inject(HttpClient);
  private readonly toastr = inject(NbToastrService);
  private readonly translate = inject(TranslateService);
  private readonly url = `${environment.tutorsBackend}/portal-agent/speak`;

  /** message being read aloud */
  readonly playingId = signal<string | null>(null);
  /** message whose audio is being made */
  readonly loadingId = signal<string | null>(null);

  private audio: HTMLAudioElement | null = null;
  private request?: Subscription;

  toggle(messageId: string, markdown: string): void {
    if (this.playingId() === messageId || this.loadingId() === messageId) {
      this.stop();
      return;
    }
    this.play(messageId, markdown);
  }

  play(messageId: string, markdown: string): void {
    this.stop();
    const text = plainText(markdown);
    if (!text) return;
    this.loadingId.set(messageId);
    this.request = this.http
      .post<{ data: { audio: string; mime_type: string } }>(this.url, { text })
      .pipe(finalize(() => this.loadingId.update((id) => (id === messageId ? null : id))))
      .subscribe({
        next: ({ data }) => {
          const audio = new Audio(`data:${data.mime_type};base64,${data.audio}`);
          const done = () => {
            if (this.audio === audio) {
              this.audio = null;
              this.playingId.set(null);
            }
          };
          audio.onended = done;
          audio.onerror = done;
          this.audio = audio;
          this.playingId.set(messageId);
          // A browser may refuse sound without a recent click (Safari after an
          // automatic answer): then the button stays for the person to press.
          audio.play().catch(done);
        },
        error: (err: HttpErrorResponse) => {
          const key = err.status === 429 ? 'agentPanel.speakLimit' : 'agentPanel.speakFailed';
          this.toastr.warning(this.translate.instant(key), '');
        },
      });
  }

  stop(): void {
    this.request?.unsubscribe();
    this.request = undefined;
    this.audio?.pause();
    this.audio = null;
    this.playingId.set(null);
    this.loadingId.set(null);
  }
}

/** What is spoken: the answer without Markdown marks — links as their text, no
 *  code blocks, table rows as "cell, cell", every line ending as a sentence so
 *  headings and list items get their pause. */
export function plainText(markdown: string): string {
  const lines = (markdown || '').replace(/```[\s\S]*?```/g, '\n').split('\n');
  const spoken: string[] = [];
  for (const raw of lines) {
    let line = raw.trim();
    if (!line || /^\|?\s*:?-{3,}/.test(line)) continue; // empty, table separator, rule
    if (line.includes('|')) {
      line = line.split('|').map((cell) => cell.trim()).filter(Boolean).join(', ');
    }
    line = line
      .replace(/^(#{1,6}|>|[-*+]|\d+[.)])\s+/, '')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/<[^>]+>/g, ' ')
      .replace(/(\*\*|__|\*|_|~~)(?=\S)([\s\S]*?\S)\1/g, '$2')
      .trim();
    if (!line) continue;
    spoken.push(/[.!?:;,…]$/.test(line) ? line : `${line}.`);
  }
  return spoken.join(' ').replace(/\s+/g, ' ').trim();
}
